import React from 'react';
import Footer from './Footer';
import Header from './Header';
import Seo from './Seo';
import './TermsOfService.css';

const TermsOfService = () => {
  return (
    <div className="neural-glass-homepage">
      <Seo
        title="Terms of Service"
        description="The terms and conditions for using Terview AI's career and interview preparation tools."
        path="/terms-of-service"
      />
      <div className="neural-background"></div>
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <Header setShowAuthModal={() => {}} />

      <main className="terms-main">

        <div className="terms-hero">
          <div className="terms-hero-logo">
            <div className="terms-hero-logo-icon">🎯</div>
            <div className="terms-hero-logo-name">terview.ai</div>
            <div className="terms-hero-logo-tagline">Instant Answers, Immediate Confidence</div>
          </div>
          <div className="terms-hero-content">
            <h1 className="terms-hero-title">Terms of Service</h1>
            <p className="terms-hero-desc">
              Please read these Terms of Service carefully before using Terview AI's platform and services. By accessing our services, you agree to be bound by these terms.
            </p>
            <div className="terms-hero-meta">
              <div>
                <div className="terms-hero-meta-label">WRITTEN BY</div>
                <div className="terms-hero-meta-author">
                  <div className="terms-hero-meta-avatar">🎯</div>
                  <span className="terms-hero-meta-name">Terview AI Team</span>
                </div>
              </div>
              <div>
                <div className="terms-hero-meta-label">EFFECTIVE DATE</div>
                <div className="terms-hero-meta-date">Mar 18, 2026</div>
              </div>
            </div>
          </div>
        </div>

        <div className="terms-body">

          <Section title="Terms of Service">
            <p><strong className="terms-highlight">Effective Date:</strong> March 18, 2026</p>
            <p><strong className="terms-highlight">Last Updated:</strong> March 18, 2026</p>
            <p>
              These Terms of Service ("Terms") govern your access to and use of the services provided by Terview Technologies Private Limited ("Company", "we", "us", or "our"), a company incorporated under the laws of India. Our services are accessible via{' '}
              <a href="https://terview.ai" className="terms-link">https://terview.ai</a> and related desktop applications.
            </p>
            <p>
              By accessing or using our services, you confirm that you are at least 18 years of age, have read and understood these Terms, and agree to be bound by them. If you do not agree, please discontinue use of our services immediately.
            </p>
          </Section>

          <Section title="About the Company">
            <p>
              Terview AI is operated by Terview Technologies Private Limited, a company registered in India. Our registered office is in India. All transactions, subscriptions, and services are governed by Indian law unless otherwise specified.
            </p>
            <ul>
              <BulletItem title="Company Name">Terview Technologies Private Limited</BulletItem>
              <BulletItem title="Country of Incorporation">India</BulletItem>
              <BulletItem title="Contact Email"><a href="mailto:support@terview.ai" className="terms-link">support@terview.ai</a></BulletItem>
              <BulletItem title="Website"><a href="https://terview.ai" className="terms-link">https://terview.ai</a></BulletItem>
            </ul>
          </Section>

          <Section title="Services Provided">
            <p>Terview AI provides AI-powered career and interview preparation tools including but not limited to:</p>
            <ul>
              <BulletItem>Real-time AI Interview Assistant</BulletItem>
              <BulletItem>Mock AI Interview sessions</BulletItem>
              <BulletItem>Resume Builder and ATS Checker</BulletItem>
              <BulletItem>Cover Letter Generator</BulletItem>
              <BulletItem>Interview Questions Generator</BulletItem>
              <BulletItem>AI Quiz and LinkedIn Optimizer</BulletItem>
              <BulletItem>AI Portfolio Builder</BulletItem>
              <BulletItem>Auto Job Apply feature</BulletItem>
              <BulletItem>Desktop applications for macOS and Windows</BulletItem>
            </ul>
          </Section>

          <Section title="User Accounts">
            <ul>
              <BulletItem title="Registration">You must provide accurate, complete, and current information when creating an account. You are responsible for maintaining the confidentiality of your login credentials.</BulletItem>
              <BulletItem title="One Account Per User">Each user may maintain only one active account. Creating multiple accounts to circumvent credit or subscription limits is prohibited.</BulletItem>
              <BulletItem title="Account Security">You are responsible for all activity that occurs under your account. Notify us immediately at support@terview.ai if you suspect unauthorized access.</BulletItem>
              <BulletItem title="Account Termination">We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or misuse our services.</BulletItem>
            </ul>
          </Section>

          <Section title="Subscriptions and Payments">
            <ul>
              <BulletItem title="Pricing">Subscription plans and pricing are displayed on our website. Prices are listed in USD and INR as applicable. Applicable taxes (including GST) will be added at checkout for Indian users.</BulletItem>
              <BulletItem title="Billing">Subscriptions are billed on a recurring basis (monthly or as selected). By subscribing, you authorize us to charge your payment method on each renewal date.</BulletItem>
              <BulletItem title="Credits">Free users receive a limited number of credits. Credits are non-transferable and expire as specified. Paid subscribers receive unlimited credits for the duration of their subscription.</BulletItem>
              <BulletItem title="Refunds">Given the digital nature of our services, refunds are generally not provided once a subscription period has commenced. If you believe you were charged in error, contact us within 7 days at support@terview.ai and we will review your case.</BulletItem>
              <BulletItem title="Payment Processors">We use Stripe and Dodo Payments to process payments securely. We do not store your full card details on our servers.</BulletItem>
              <BulletItem title="GST">For users in India, applicable Goods and Services Tax (GST) will be charged as per Indian tax regulations.</BulletItem>
            </ul>
          </Section>

          <Section title="Acceptable Use Policy">
            <p>You agree NOT to use our services to:</p>
            <ul>
              <BulletItem>Cheat, deceive, or misrepresent yourself in actual job interviews beyond practicing and preparation.</BulletItem>
              <BulletItem>Violate any applicable local, state, national, or international law or regulation.</BulletItem>
              <BulletItem>Upload or transmit malicious code, viruses, or harmful content.</BulletItem>
              <BulletItem>Scrape, copy, or reverse engineer any part of our platform.</BulletItem>
              <BulletItem>Resell or commercialize our services without prior written consent.</BulletItem>
              <BulletItem>Harass, abuse, or harm other users or our team.</BulletItem>
              <BulletItem>Attempt to gain unauthorized access to our systems or other user accounts.</BulletItem>
            </ul>
          </Section>

          <Section title="Intellectual Property">
            <p>
              All content, features, and functionality on Terview AI — including but not limited to software, text, graphics, logos, and AI-generated outputs — are owned by Terview Technologies Private Limited and are protected under Indian and international intellectual property laws.
            </p>
            <ul>
              <BulletItem title="Your Content">You retain ownership of any resume, cover letter, or career content you upload. By uploading content, you grant us a limited, non-exclusive license to process it solely for providing the requested service.</BulletItem>
              <BulletItem title="AI Output">AI-generated responses, suggestions, and content produced by our platform are provided for personal use only. You may not sell or redistribute AI-generated output as your own original work.</BulletItem>
              <BulletItem title="Feedback">If you provide feedback or suggestions, we may use them to improve our services without obligation or compensation to you.</BulletItem>
            </ul>
          </Section>

          <Section title="Disclaimer of Warranties">
            <p>
              Our services are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied. We do not guarantee that:
            </p>
            <ul>
              <BulletItem>Our services will be uninterrupted, error-free, or secure at all times.</BulletItem>
              <BulletItem>AI-generated responses will be accurate, complete, or suitable for your specific situation.</BulletItem>
              <BulletItem>Use of our platform will guarantee job interview success or employment.</BulletItem>
            </ul>
          </Section>

          <Section title="Limitation of Liability">
            <p>
              To the maximum extent permitted by applicable Indian law, Terview Technologies Private Limited shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services, including but not limited to loss of data, loss of profits, or loss of employment opportunities.
            </p>
            <p>
              Our total liability to you for any claims arising under these Terms shall not exceed the amount you paid to us in the 3 months preceding the claim.
            </p>
          </Section>

          <Section title="Governing Law and Dispute Resolution">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.
            </p>
            <ul>
              <BulletItem title="Jurisdiction">Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts located in India.</BulletItem>
              <BulletItem title="Arbitration">We encourage resolving disputes amicably. For unresolved disputes, arbitration may be pursued under the Arbitration and Conciliation Act, 1996 of India.</BulletItem>
              <BulletItem title="Consumer Rights">Nothing in these Terms limits your rights under the Consumer Protection Act, 2019 (India) or other applicable Indian consumer protection laws.</BulletItem>
            </ul>
          </Section>

          <Section title="Privacy">
            <p>
              Your use of our services is also governed by our{' '}
              <a href="/privacy-policy" className="terms-link">Privacy Policy</a>, which is incorporated into these Terms by reference. By using our services, you consent to the collection and use of your data as described in our Privacy Policy.
            </p>
          </Section>

          <Section title="Modifications to Terms">
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or a prominent notice on our website. Your continued use of our services after any changes constitutes your acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="Termination">
            <p>
              We may suspend or terminate your access to our services at our discretion, with or without notice, if you violate these Terms or engage in conduct that we deem harmful to our platform or other users. Upon termination, your right to use our services ceases immediately.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <ul>
              <BulletItem title="Email"><a href="mailto:support@terview.ai" className="terms-link">support@terview.ai</a></BulletItem>
              <BulletItem title="Website"><a href="https://terview.ai" className="terms-link">https://terview.ai</a></BulletItem>
              <BulletItem title="Company">Terview Technologies Private Limited, India</BulletItem>
            </ul>
            <p className="terms-note">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </Section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="terms-section">
    <h2>{title}</h2>
    {children}
  </div>
);

const BulletItem = ({ title, children }) => (
  <li>
    {title && <strong>{title}: </strong>}
    {children}
  </li>
);

export default TermsOfService;